import {
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box } from '@material-ui/core';

import { MetadataKeyValueInput } from '../components/MetadataKeyValueInput';
import { APPLICATION_METADATA_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import {
  KubernetesMetadata,
  MetadataKeyValuePair,
  MetadataField,
  DEFAULT_METADATA,
} from '../types';
import { metadataInputErrors } from '../validation';

interface MetadataSectionProps {
  value: KubernetesMetadata | undefined;
  onChange: (value: KubernetesMetadata) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

const MetadataSectionInner = ({
  value,
  onChange,
  errors,
  enableAppSetSupport = false,
}: MetadataSectionProps) => {
  const classes = useStyles();
  const sectionRef = useRef<HTMLDivElement>(null);
  const metadataError =
    errors.length > 0
      ? metadataInputErrors(value ?? DEFAULT_METADATA, { enableAppSetSupport })
      : '';
  const metadata = value ?? DEFAULT_METADATA;

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (metadataError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [metadataError]);

  useEffect(() => {
    if (!value) {
      onChangeRef.current(DEFAULT_METADATA);
    }
  }, [value]);

  const handleAddField = useCallback((field: MetadataField) => {
    const m = metadataRef.current;
    onChangeRef.current({
      ...m,
      [field]: [...(m[field] || []), { key: '', value: '' }],
    });
  }, []);

  const handleRemoveField = useCallback(
    (field: MetadataField, index: number) => {
      const m = metadataRef.current;
      onChangeRef.current({
        ...m,
        [field]: (m[field] || []).filter((_, i) => i !== index),
      });
    },
    [],
  );

  const handleChangeField = useCallback(
    (
      field: MetadataField,
      index: number,
      property: keyof MetadataKeyValuePair,
      fieldValue: string,
    ) => {
      const m = metadataRef.current;
      onChangeRef.current({
        ...m,
        [field]: (m[field] || []).map((item, i) =>
          i === index ? { ...item, [property]: fieldValue } : item,
        ),
      });
    },
    [],
  );

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Application Metadata"
          description="Metadata to add to the Application resource created by this template."
          divider
          infoDoc={APPLICATION_METADATA_INFO}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Annotations"
            description="Manage annotations for the application's destination resources."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="annotation"
          items={metadata.annotations || []}
          disabled={false}
          onAdd={() => handleAddField('annotations')}
          onRemove={index => handleRemoveField('annotations', index)}
          onChange={(index, property, fieldValue) =>
            handleChangeField('annotations', index, property, fieldValue)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Labels"
            description="Manage labels for the application's destination resources."
          />
        </Box>
        <MetadataKeyValueInput
          inputType="label"
          items={metadata.labels || []}
          disabled={false}
          onAdd={() => handleAddField('labels')}
          onRemove={index => handleRemoveField('labels', index)}
          onChange={(index, property, fieldValue) =>
            handleChangeField('labels', index, property, fieldValue)
          }
          errors={metadataError ? [metadataError] : []}
          enableAppSetSupport={enableAppSetSupport}
        />
        <Box className={classes.inputContainer}>
          {metadataError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={metadataError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const MetadataSection = React.memo(MetadataSectionInner);
